import type { AssetResponse, BlockchainConfig } from '@ping/api';
import { AssetVisibility } from '@ping/api';

/**
 * Deposit / withdraw asset picker rules (UI layer).
 *
 * FO `/assets-info` already omit-filters by CanAccess for the caller's principal.
 * This module is fail-closed defense-in-depth for Invisible leakage and encodes
 * network CanDeposit / CanWithdraw selection rules used by CryptoTransaction.
 */

export type DepositWithdrawActor = 'public' | 'tagged';

/** Expected FO catalog shape for deposit/withdraw asset pickers (CanAccess). */
export function foCatalogIncludesAsset(visibility: string | undefined | null, actor: DepositWithdrawActor): boolean {
  const v = String(visibility ?? '').toLowerCase();
  if (v === 'invisible' || v === '0') return false;
  if (v === 'internal' || v === '2') return actor === 'tagged';
  // Visible, missing, or unknown FO-side unknown is fail-closed at FO;
  // UI treats non-internal/non-invisible as listed when FO returned the row.
  return v === 'visible' || v === '1' || v === '';
}

/** Client defense: never offer Invisible; Internal ok when FO already returned it. */
export function isDepositWithdrawAssetListed(
  asset: AssetResponse | { blockchain_configs?: BlockchainConfig[] | null; id?: string }
): boolean {
  return (asset.blockchain_configs ?? []).some(isReturnedNetworkAccessible);
}

export function filterDepositWithdrawAssets<T extends { blockchain_configs?: BlockchainConfig[] | null }>(
  assets: T[] | null | undefined
): T[] {
  return (assets ?? []).filter(isDepositWithdrawAssetListed);
}

/** ReceiveTransaction: NetworkSelect ignore when can_deposit === false */
export function isDepositNetworkDisabled(network: BlockchainConfig | null | undefined): boolean {
  return network?.can_deposit === false || !isReturnedNetworkAccessible(network);
}

/** SendTransaction: NetworkSelect ignore when can_withdraw === false */
export function isWithdrawNetworkDisabled(network: BlockchainConfig | null | undefined): boolean {
  return network?.can_withdraw === false || !isReturnedNetworkAccessible(network);
}

function isReturnedNetworkAccessible(network: BlockchainConfig | null | undefined): boolean {
  if (!network) return false;
  const visibility = String(network.visibility ?? '').toLowerCase();
  return (
    visibility === '' ||
    visibility === 'visible' ||
    visibility === '1' ||
    visibility === 'internal' ||
    visibility === '2'
  );
}

export function selectableDepositNetworks(networks: BlockchainConfig[] | null | undefined): BlockchainConfig[] {
  return (networks ?? []).filter(n => !isDepositNetworkDisabled(n));
}

export function selectableWithdrawNetworks(networks: BlockchainConfig[] | null | undefined): BlockchainConfig[] {
  return (networks ?? []).filter(n => !isWithdrawNetworkDisabled(n));
}

/**
 * Full deposit execution eligibility once FO returned the asset + network.
 * Mirrors FO WalletService.CanDeposit (scope + network). Tagged is implied by FO catalog for Internal.
 */
export function canRequestDepositAddress(params: {
  visibility?: string | null;
  actor: DepositWithdrawActor;
  networkCanDeposit: boolean;
}): boolean {
  if (!params.networkCanDeposit) return false;
  return foCatalogIncludesAsset(params.visibility, params.actor);
}

/**
 * Full withdraw eligibility once FO returned the asset + network.
 * FoundInternal network bypass is server-side only (not modeled in NetworkSelect UI).
 */
export function canSubmitExternalWithdraw(params: {
  visibility?: string | null;
  actor: DepositWithdrawActor;
  networkCanWithdraw: boolean;
}): boolean {
  if (!params.networkCanWithdraw) return false;
  return foCatalogIncludesAsset(params.visibility, params.actor);
}

/** Deposit API opaque lock message patterns shown in DepositErrorAlert */
export function isOpaqueDepositLockError(message: string | null | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return m.includes('locked') || m.includes('does not exist') || m.includes('not found');
}

type MalAssetFixtureOverrides = Partial<AssetResponse> & {
  id: string;
  /** Fixture convenience only; production visibility lives on each blockchain_config. */
  visibility?: AssetVisibility | string | null;
  hidden?: boolean;
};

export function buildMalAssetFixture(overrides: MalAssetFixtureOverrides): AssetResponse {
  const { visibility: fixtureVisibility, blockchain_configs: fixtureConfigs, ...assetOverrides } = overrides;
  delete (assetOverrides as { hidden?: boolean }).hidden;
  // Asset-level visibility is a test convenience that fans down onto configs.
  const visibility = (fixtureVisibility as AssetVisibility | undefined) ?? AssetVisibility.Visible;
  const blockchainConfigs = (
    fixtureConfigs ?? [
      {
        blockchain_network: 'CoreBlockchain',
        can_deposit: true,
        can_withdraw: true,
        confirmation_count: 20,
        min_withdraw: 0.001,
        withdrawal_fee: 0.1,
      },
    ]
  ).map(config => ({ ...config, visibility: config.visibility ?? visibility }));

  return {
    asset_name: overrides.asset_name ?? overrides.id,
    id: overrides.id,
    blockchain_configs: blockchainConfigs,
    ...assetOverrides,
  };
}
